import React, { useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { formatDateDMY } from '../../utils/dateFormat';

/**
 * PaymentReceiptPrint — printable customer-facing receipt that matches the
 * Tax Invoice / Purchase Order layout exactly:
 *   • Top-left corner: company logo + CR / VAT registration lines.
 *   • Top-right corner: document header banner image (company.document_header_url).
 *   • Bottom edge: document footer banner image (company.document_footer_url).
 *   • Same A4 margins and "letterhead clearance" so the print fits the same
 *     official stationery the Invoice/PO prints use.
 *
 * Rendered into a hidden iframe and print() is fired only after every <img>
 * has loaded — same anti-flash gate as InvoicePrintView.
 */
const PaymentReceiptPrint = ({ payment, company, currencyCode, formatAmount, onClose }) => {
  const iframeRef = useRef(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument;
    doc.open();
    doc.write('<!DOCTYPE html><html><head><title></title></head><body><div id="root"></div></body></html>');
    doc.close();
  }, []);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const mount = iframe.contentDocument?.getElementById('root');
    if (!mount) return;
    const reactRoot = createRoot(mount);
    reactRoot.render(
      <ReceiptLayout
        payment={payment}
        company={company}
        currencyCode={currencyCode}
        formatAmount={formatAmount}
      />
    );

    // Wait for every <img> in the iframe (logo, header banner, footer banner)
    // to finish loading before firing print() — same race-condition fix
    // already in InvoicePrintView.
    let cancelled = false;
    const fireAfterImages = () => {
      const idoc = iframe.contentDocument;
      if (!idoc) return;
      const imgs = Array.from(idoc.images || []);
      const waiters = imgs.map((img) => (
        img.complete && img.naturalWidth > 0
          ? Promise.resolve()
          : new Promise((res) => {
              const done = () => res();
              img.addEventListener('load', done, { once: true });
              img.addEventListener('error', done, { once: true });
              setTimeout(done, 4000);
            })
      ));
      Promise.all(waiters).then(() => {
        if (cancelled) return;
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch {
          /* user can manually print */
        }
      });
    };
    const t = setTimeout(fireAfterImages, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
      try { reactRoot.unmount(); } catch { /* noop */ }
    };
  }, [payment, company, currencyCode, formatAmount]);

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4" data-testid="payment-print-modal">
      <div className="bg-white rounded shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col">
        <div className="px-4 py-2 border-b flex items-center justify-between">
          <h3 className="font-semibold">Print preview · Payment Receipt</h3>
          <div className="flex gap-2">
            <button
              onClick={() => iframeRef.current?.contentWindow?.print()}
              className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              data-testid="payment-print-btn"
            >
              🖨 Print / Save as PDF
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1.5 border rounded text-sm hover:bg-gray-50"
              data-testid="payment-print-close-btn"
            >
              Close
            </button>
          </div>
        </div>
        <iframe ref={iframeRef} title="Payment receipt print" className="flex-1 w-full bg-gray-200" />
      </div>
    </div>
  );
};

/* ---------- Number → English words ---------- */
const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
  'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

const numToWords = (num) => {
  if (num === 0) return 'zero';
  if (num < 20) return ones[num];
  if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? ' ' + ones[num % 10] : '');
  if (num < 1000) return ones[Math.floor(num / 100)] + ' hundred' + (num % 100 ? ' ' + numToWords(num % 100) : '');
  if (num < 1000000) return numToWords(Math.floor(num / 1000)) + ' thousand' + (num % 1000 ? ' ' + numToWords(num % 1000) : '');
  return String(num);
};

const amountInWords = (amount, currencyCode = 'BHD') => {
  const decimals = currencyCode === 'BHD' ? 3 : 2;
  const fractionName = currencyCode === 'BHD' ? 'fils' : 'cents';
  const safe = Math.max(0, parseFloat(amount) || 0);
  const whole = Math.floor(safe);
  const frac = Math.round((safe - whole) * Math.pow(10, decimals));
  let str = numToWords(whole) + ' ' + currencyCode;
  if (frac > 0) str += ' and ' + numToWords(frac) + ' ' + fractionName;
  str += ' only';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/* ---------- Print layout ---------- */
const ReceiptLayout = ({ payment, company, currencyCode, formatAmount }) => {
  const isAdv = (payment.payment_type || 'invoice_payment') === 'advance';
  const cur = currencyCode || company?.currency || 'BHD';
  const received = parseFloat(payment.received_amount) || 0;
  const isCancelled =
    payment.cancellation_status === 'cancelled' ||
    (payment.payment_status || '').toLowerCase() === 'cancelled';
  const watermark = isCancelled ? 'CANCELLED' : null;

  return (
    <>
      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; font-family: 'Segoe UI', Arial, sans-serif; color: #111; background: #fff; }
        .page {
          display: flex;
          flex-direction: column;
          /* Same letterhead clearance as InvoicePrintView */
          min-height: 297mm;
          padding: 50mm 12mm 60mm 12mm;
          box-sizing: border-box;
          position: relative;
          font-size: 11px;
        }
        /* Top-right header banner */
        .banner-top, .banner-bottom { position: absolute; z-index: 1; }
        .banner-top {
          top: 8mm; right: 12mm;
          width: 60mm; height: 28mm;
          display: flex; align-items: center; justify-content: flex-end;
        }
        .banner-bottom { bottom: 0; left: 0; right: 0; text-align: center; }
        .banner-top img { max-width: 100%; max-height: 100%; object-fit: contain; display: block; }
        .banner-bottom img { width: 100%; max-height: 22mm; object-fit: contain; display: block; }
        /* Top-left logo + CR/VAT registration block */
        .logo-left {
          position: absolute;
          top: 8mm; left: 12mm;
          width: 86mm;
        }
        .logo-left .logo-box {
          width: 60mm; height: 28mm;
          display: flex; align-items: center; justify-content: flex-start;
        }
        .logo-left .logo-box img { max-width: 100%; max-height: 100%; object-fit: contain; display: block; }
        .logo-left .reg-block { margin-top: 2mm; font-size: 9pt; color: #111; line-height: 1.45; }
        .logo-left .reg-block .lbl { color: #6b7280; }
        .logo-left .reg-block .val { font-weight: 700; color: #111; }
        /* Watermark (CANCELLED) */
        .wm {
          position: absolute; inset: 0;
          display: flex; align-items: center; justify-content: center;
          pointer-events: none; z-index: 5;
        }
        .wm span {
          font-weight: 900; font-size: 180pt;
          color: rgba(180,30,30,0.10);
          transform: rotate(-30deg);
          letter-spacing: 0.1em; white-space: nowrap;
        }
        .doc-title { text-align: center; font-size: 16pt; font-weight: 800; letter-spacing: 2px; margin: 0 0 4mm; color: #111; }
        .ref-bar {
          display: flex; justify-content: space-between; gap: 8mm;
          border: 1px solid #d1d5db; padding: 6px 10px; border-radius: 4px;
          margin-bottom: 6mm; font-size: 10pt;
        }
        .ref-bar .label { color: #6b7280; font-size: 8pt; text-transform: uppercase; letter-spacing: 0.5px; }
        .ref-bar .value { color: #111; font-weight: 700; }
        .party {
          border: 1px solid #d1d5db; padding: 8px 12px; border-radius: 4px;
          margin-bottom: 6mm;
        }
        .party h4 { margin: 0 0 4px; font-size: 8pt; color: #6b7280; text-transform: uppercase; letter-spacing: 0.6px; }
        .party .name { font-weight: 700; font-size: 12pt; }
        table { width: 100%; border-collapse: collapse; margin-top: 2mm; }
        th, td { border: 1px solid #d1d5db; padding: 7px 10px; text-align: left; font-size: 10pt; }
        th { background: #f3f4f6; font-size: 8pt; color: #4b5563; text-transform: uppercase; letter-spacing: 0.4px; }
        .amt-cell { text-align: right; font-weight: 600; font-family: 'Consolas','Menlo',monospace; }
        .total-row td { background: #eff6ff; font-weight: 700; font-size: 11pt; }
        .words {
          margin-top: 4mm; padding: 6px 10px;
          background: #f9fafb; border-left: 3px solid #1d4ed8;
          font-style: italic; font-size: 10pt;
        }
        .meta-line { margin-top: 3mm; font-size: 10pt; color: #4b5563; }
        .sigs {
          display: flex; justify-content: space-between; gap: 40mm;
          margin-top: auto; padding-top: 14mm;
        }
        .sigs .col {
          flex: 1; text-align: center;
          border-top: 1px solid #111;
          padding-top: 3px; font-size: 9pt;
        }
        @media print {
          @page { size: A4; margin: 0; }
          html, body { margin: 0; padding: 0; }
          .banner-bottom { position: fixed !important; bottom: 0 !important; left: 0; right: 0; }
          .sigs {
            position: fixed !important;
            bottom: 28mm !important;
            left: 12mm; right: 12mm;
            margin-top: 0 !important; padding-top: 0 !important;
          }
        }
      `}</style>
      <div className="page">
        {/* Top-left: logo + CR/VAT block */}
        {(company?.logo_url || company?.cr_number || company?.cr_number_2 || company?.tax_id) && (
          <div className="logo-left">
            {company.logo_url && (
              <div className="logo-box">
                <img src={company.logo_url} alt={company.name || ''} />
              </div>
            )}
            {(company.cr_number || company.cr_number_2 || company.tax_id) && (
              <div className="reg-block">
                {company.cr_number && (
                  <div><span className="lbl">CR No 1:</span> <span className="val">{company.cr_number}</span></div>
                )}
                {company.cr_number_2 && (
                  <div><span className="lbl">CR No 2:</span> <span className="val">{company.cr_number_2}</span></div>
                )}
                {company.tax_id && (
                  <div><span className="lbl">VAT No:</span> <span className="val">{company.tax_id}</span></div>
                )}
              </div>
            )}
          </div>
        )}
        {/* Top-right header banner */}
        {company?.document_header_url && (
          <div className="banner-top"><img src={company.document_header_url} alt="" /></div>
        )}
        {/* Bottom footer banner */}
        {company?.document_footer_url && (
          <div className="banner-bottom"><img src={company.document_footer_url} alt="" /></div>
        )}
        {watermark && (
          <div className="wm"><span>{watermark}</span></div>
        )}

        <div className="doc-title">{isAdv ? 'ADVANCE RECEIPT' : 'PAYMENT RECEIPT'}</div>

        <div className="ref-bar">
          <div>
            <div className="label">Record No.</div>
            <div className="value">{payment.record_no || (payment.id || '').slice(0, 8).toUpperCase() || '—'}</div>
          </div>
          {payment.receipt_no && (
            <div>
              <div className="label">Receipt No</div>
              <div className="value">{payment.receipt_no}</div>
            </div>
          )}
          <div>
            <div className="label">Date</div>
            <div className="value">{payment.received_date ? formatDateDMY(payment.received_date) : '—'}</div>
          </div>
          <div>
            <div className="label">{isAdv ? 'Against PO' : 'Against Invoice'}</div>
            <div className="value">{isAdv ? (payment.po_number || '—') : (payment.invoice_number || '—')}</div>
          </div>
          <div>
            <div className="label">Mode</div>
            <div className="value">
              {payment.payment_mode}
              {payment.payment_mode === 'Cheque' && payment.cheque_no ? ` · ${payment.cheque_no}` : ''}
              {payment.payment_mode === 'Bank Transfer' && payment.bank_name ? ` · ${payment.bank_name}` : ''}
            </div>
          </div>
        </div>

        <div className="party">
          <h4>Received From</h4>
          <div className="name">{payment.customer_name || '—'}</div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th style={{ width: '40mm', textAlign: 'right' }}>Amount ({cur})</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                {isAdv
                  ? <>Advance receipt against PO <b>{payment.po_number || ''}</b>{payment.notes ? ` — ${payment.notes}` : ''}</>
                  : <>Payment for Sales Invoice <b>#{payment.invoice_number}</b></>
                }
              </td>
              <td className="amt-cell">{formatAmount ? formatAmount(received, { showSymbol: false }) : received.toFixed(3)}</td>
            </tr>
            {!isAdv && (payment.balance_amount || 0) > 0 && (
              <tr>
                <td style={{ color: '#6b7280' }}>Outstanding balance after this payment</td>
                <td className="amt-cell" style={{ color: '#b91c1c' }}>
                  {formatAmount ? formatAmount(payment.balance_amount, { showSymbol: false }) : (payment.balance_amount || 0).toFixed(3)}
                </td>
              </tr>
            )}
            <tr className="total-row">
              <td style={{ textAlign: 'right' }}>Total Received ({cur})</td>
              <td className="amt-cell">{formatAmount ? formatAmount(received, { showSymbol: false }) : received.toFixed(3)}</td>
            </tr>
          </tbody>
        </table>

        <div className="words">{amountInWords(received, cur)}</div>

        {payment.payment_mode === 'Cheque' && (payment.cheque_no || payment.cheque_date || payment.cheque_deposit_date) && (
          <div className="meta-line">
            Cheque #{payment.cheque_no || '—'} dated {payment.cheque_date ? formatDateDMY(payment.cheque_date) : '—'}
            {payment.cheque_deposit_date ? `, deposited on ${formatDateDMY(payment.cheque_deposit_date)}` : ''}
          </div>
        )}
        {payment.payment_mode === 'Bank Transfer' && (payment.bank_name || payment.transfer_date) && (
          <div className="meta-line">
            Bank transfer via {payment.bank_name || '—'} on {payment.transfer_date ? formatDateDMY(payment.transfer_date) : '—'}
          </div>
        )}
        {payment.notes && !isAdv && (
          <div className="meta-line"><b>Notes:</b> {payment.notes}</div>
        )}

        <div className="sigs">
          <div className="col">Received By</div>
          <div className="col">Customer Signature</div>
        </div>
      </div>
    </>
  );
};

export default PaymentReceiptPrint;
